import lodash from 'lodash';
window._ = lodash;

/**
 * We'll load the axios HTTP library which allows us to easily issue requests
 * to our Laravel back-end. This library automatically handles sending the
 * CSRF token as a header based on the value of the "XSRF" token cookie.
 */

import axios from 'axios';

/**
 * Axios használat – RÉGI vs ÚJ (kompatibilitás miatt mindkettő megvan)
 *
 * 1) RÉGI MINTA (legacy)
 *    - sok régi komponensben nincs `import axios`, hanem csak simán `axios.get(...)`
 *    - ez azért működik, mert itt globálissá tesszük: window.axios
 *
 *    Példa:
 *      axios.get('/sanctum/csrf-cookie')
 *      axios.post(this.$configuration.get('urls.login'), data)
 *
 * 2) ÚJ MINTA (javasolt)
 *    - külön axios instance: axiosInstance
 *    - tisztább, bővíthető (pl. extra metódusok, külön defaultok)
 *
 *    Példa:
 *      axiosInstance.get('/sanctum/csrf-cookie')
 *      axiosInstance.post(this.$configuration.get('urls.login'), data)
 *      axiosInstance.jsonPatch('/api/v1/rest/users/1', payload)
 */

// --------------------
// 1) RÉGI (legacy) – hagyjuk meg
// --------------------
window.axios = axios;
window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
axios.defaults.maxRedirects = 0; // fetch redirect: "manual"
axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;

// --------------------
// 2) ÚJ (javasolt) – külön instance
// --------------------
export const axiosInstance = axios.create({
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
        // 'Content-Type': 'application/json', // ha kell később
    },
    withCredentials: true,
    withXSRFToken: true,
    maxRedirects: 0
});

/**
 * Extra helper: JSON Patch endpoint
 * használat: axiosInstance.jsonPatch(url, patchArray, options)
 */
axiosInstance.jsonPatch = function (url, data, options) {
    for (let key in data) {
        const entry = data[key];

        if (!entry?.op?.length || !entry?.path?.length) {
            console.error(`Payload has invalid format at index ${key}`);
            console.log(entry);
            console.warn('Aborting patch request');
            return;
        }
    }

    return this.patch(url + '/json-patch', data, options);
};

function setAxiosIntercreptor(Application, client = axios) {
    client.interceptors.request.use((config) => {
        if (!config.url) {
            return config;
        }

        if (config.urlParams) {

            // 1️⃣ Fragment-alapú csere (biztonságos path szintű)
            const separator = '/';
            let fragments = config.url.split(separator);

            fragments = fragments.map(fragment => {
                if (fragment.startsWith(':')) {
                    const key = fragment.slice(1);
                    if (config.urlParams[key] !== undefined) {
                        return encodeURIComponent(String(config.urlParams[key]));
                    }
                }
                return fragment;
            });

            config.url = fragments.join(separator);

            // 2️⃣ Fallback globális csere (ha valami bent maradt)
            Object.entries(config.urlParams).forEach(([key, value]) => {
                const regex = new RegExp(`:${key}(?=/|$)`, 'g');
                config.url = config.url.replace(regex, encodeURIComponent(String(value)));
            });
        }

        return config;
    });

    // Add a response interceptor
    client.interceptors.response.use(
        // In case of 2xx
        undefined, // Do nothing with it here
        // Outside 2xx status
        (error, x) => {
            if (!error.hasOwnProperty('response')) {
                console.error('Missing response in axios error. This interceptor is unable to handle it. Check if the provided request url is not empty! Trace:');
                console.trace();

                return Promise.reject(error);
            }

            switch (error.response.status) {
                case 302: // Transforming relocation order into a valid UI action instead of an error
                    if (error.response.data.accepted) {
                        /**
                         * Vue.js will not perform relocation two times if the original action would be
                         * a relocation too to the same url as in the Login component.
                         * Otherwise, double relocation may can happen.
                         */
                        Application.config.globalProperties.$router.replace({
                            path: (error.response.headers.get("content-type") === 'application/json') ?
                                error.response.data.relocate :
                                error.response.data
                        });

                        // This is not an error to be honest but a valid redirection response
                        return Promise.resolve(error.response);
                    }
                    break;
                case 401: // Authentication is required so take the user to the login page
                    let resolved = Application.config.globalProperties.$router.resolve({ name: 'login' });
                    window.location.replace(resolved.fullPath); // A regular router redirect is not enough
                    break;
            }

            return Promise.reject(error);
        }
    );
}

/**
 * Echo exposes an expressive API for subscribing to channels and listening
 * for events that are broadcast by Laravel. Echo and event broadcasting
 * allows your team to easily build robust real-time web applications.
 */

import { createApp } from 'vue/dist/vue.esm-bundler';
import { createI18n } from 'vue-i18n';
import Cookie from "js-cookie";
import Configuration from "./plugins/Configuration.js";
import FormatterPlugin from "./plugins/Formatter";
import UserPlugin from "./plugins/User.js";
import BroadcastChannelPlugin from "./plugins/BroadcastChannel.js";
import ReverbChannelPlugin from "./plugins/ReverbChannel.js";
import * as VueRouter from "vue-router";
import { createPinia } from "pinia";
import CommonFrame from "./pages/common/Frame.vue";
import * as BroadcastAbles from "./classes/Broadcastables.js";

// ---------------------------------------
// Laravel Echo központi betöltése
// ---------------------------------------
// Az Echo inicializálása az echo.js fájlban történik,
// annak központi importja pedig itt, az appCommon.js-ben van.
//
// Megjegyzés:
// Az appAuthenticated.js is ezt a fájlt importálja,
// Lásd: appAuthenticated.js → import './appCommon.js';
import "./echo.js";

import { useResourceStore } from "./stores/resourceStore.js";
import Validator from "./plugins/Validator.js";

const env = {
    silentTranslationWarn: import.meta.env.VITE_VUEJS_I18N_SUPPRESS_WARNING_TRANSLATION?.toLowerCase() === 'true',
    silentFallbackWarn: import.meta.env.VITE_VUEJS_I18N_SUPPRESS_WARNING_FALLBACK?.toLowerCase() === 'true'
};

let currentLocale = window.frontendConfiguration.vueJs.localization.current ?? 'en';

/**
 * ⚠️ IDEIGLENESEN NEM VESSZÜK ÁT AZ "ÚJ" vue-i18n KONFIGOT
 *
 * Megjegyzés:
 * Volt egy új i18n inicializálás (legacy: false + warning beállítások),
 * de a bevezetésekor több UI elem eltűnt / szétesett (pl. Semmelweis logó, szövegek).
 *
 * Emiatt a régi/aktuális, stabil createI18n() konfiguráció van használatban.
 * Az új megoldást később – ha lesz rá idő – újra átnézzük és biztonságosan migráljuk.
 *
 * TODO: i18n konfig migráció (UI regressziók javítása után)
 */
// const I18n = createI18n({
//     fallbackLocale: window.frontendConfiguration.vueJs.localization.fallback ?? 'en',
//     // silentTranslationWarn: env.silentTranslationWarn, // for legacy true
//     // silentFallbackWarn: env.silentFallbackWarn, // for legacy true
//     formatFallbackMessages: true,
//     legacy: false, // "legacy: false" will disable component based localizations, probably due to a bug in the i18n library
//     missingWarn: !env.silentTranslationWarn, // for legacy false
//     fallbackWarn: !env.silentFallbackWarn, // for legacy false,
// });

const I18n = createI18n({
    fallbackLocale: window.frontendConfiguration.vueJs.localization.fallback ?? 'en',
    silentTranslationWarn: env.silentTranslationWarn,
    silentFallbackWarn: env.silentFallbackWarn,
    formatFallbackMessages: true
});

import PrimeVue from 'primevue/config';
import Aura from '@primeuix/themes/aura';
import ToastService from "primevue/toastservice";

export async function setApplicationDefaults(Application, routes) {
    const App = Application ?? createApp({
        components: { CommonFrame },
        template: '<CommonFrame/>',

        data() {
            return {
                defaultBroadcastListenerId: null
            };
        },

        async beforeMount() {

            this.defaultBroadcastListenerId = this.$broadcast.add((bag, event) => {
                switch (true) {
                    case bag instanceof BroadcastAbles.LoginEvent:
                    case bag instanceof BroadcastAbles.LogoutEvent:
                        window.location = bag.data;
                        break;
                }
            });
        },

        beforeUnmount() {
            this.$broadcast.remove(this.defaultBroadcastListenerId);
        },

        mounted() {
            new BroadcastAbles.ApplicationMounted().dispatch();
        }
    });

    App.provide('Application', App);

    App.use(createPinia());

    const Router = new VueRouter.createRouter({
        history: VueRouter.createWebHistory(),
        routes: routes
    });

    const User = new UserPlugin();

    const getRouterGuardInjections = (to, from) => ({
        Application: App,
        to,
        from
    });

    Router.beforeEach(async (to, from, next) => {
        for (const route of to.matched ?? []) {
            if (await route.meta.beforeEach?.(getRouterGuardInjections(to, from)) === false) {
                return next(false);
            }
        }
        next();
    });

    Router.beforeResolve(async (to, from) => {
        // opcionális meta-hook (ha nincs, nem csinál semmit)
        await to.meta.beforeResolve?.(getRouterGuardInjections(to, from));

        const resourceStore = useResourceStore();
        let hasPermission = true;

        // console.info(`Ellenőrzés indítása: Útvonal - ${to.name}, Permissions és Roles ellenőrzés`);

        // Permissions ellenőrzés
        to.matched.forEach((Route) => {
            if (Route.meta.permissions?.length) {
                // console.info(`Permissions ellenőrzés: ${Route.meta.permissions}`);

                // Kicsomagoljuk a Proxy-t egy sima tömbbe
                const userPermissions = [...resourceStore.getCurrentUser()?.data?.permissions ?? []];

                if (!userPermissions.length) {
                    // console.error("Felhasználó jogosultságai hiányoznak vagy nem betöltöttek.");
                    hasPermission = false;
                    return;
                }

                // console.info("Felhasználó aktuális jogosultságai:", userPermissions);

                // Ellenőrzés a jogosultságok alapján
                hasPermission = Route.meta.permissions.every(permission => userPermissions.includes(permission));

                // console.info(`Permissions ellenőrzés eredménye: ${hasPermission}`);
            } else {
                // console.info(`Ehhez az útvonalhoz (${Route.name}) nem szükséges permissions.`);
            }

            if (!hasPermission) {
                // console.error(`Permissions ellenőrzése meghiúsult az útvonalon: ${Route.name}`);
                return;
            }
        });

        // console.info('Permissions végső érték:', hasPermission);

        let hasRole = true;

        // Roles ellenőrzés
        to.matched.forEach((Route) => {
            if (Route.meta.roles?.length) {
                // console.info(`Roles ellenőrzés: ${Route.meta.roles}`);

                // Kicsomagoljuk a Proxy-t a szerepköröknél is
                const userRoles = [...resourceStore.getCurrentUser()?.data?.roles ?? []];

                if (!userRoles.length) {
                    // console.error("Felhasználó szerepkörei hiányoznak vagy nem betöltöttek.");
                    hasRole = false;
                    return;
                }

                // console.info("Felhasználó aktuális szerepkörei:", userRoles);

                // Ellenőrzés a szerepkörök alapján
                hasRole = resourceStore.getCurrentUser().hasRole(Route.meta.roles, { route: { to, from } });

                // console.info(`Roles ellenőrzés eredménye: ${hasRole}`);
            } else {
                // console.info(`Ehhez az útvonalhoz (${Route.name}) nem szükséges roles.`);
            }

            if (!hasRole) {
                // console.error(`Roles ellenőrzése meghiúsult az útvonalon: ${Route.name}`);
                return;
            }
        });

        // console.info('Roles végső érték:', hasRole);

        // Ha nincs jogosultság vagy szerepkör, visszaadjuk a 403 hibát
        if (!hasPermission || !hasRole) {
            console.error(`403 - Permission vagy Role ellenőrzés sikertelen. Permission: ${hasPermission}, Role: ${hasRole}`);
            return { name: 'http.status.403' };
        }

        // opcionális meta-hook (ha nincs, nem csinál semmit)
        to.meta.afterResolved?.(getRouterGuardInjections(to, from));

        // console.info(`Ellenőrzés sikeres: belépés engedélyezett az útvonalra: ${to.name}`);
        return true;
    });

    Router.afterEach(async (to, from, failure) => {
        await to.meta.afterEach?.(getRouterGuardInjections(to, from));
    });

    const Config = new Configuration(I18n);

    let cookieName = Config.get('vueJs.localization.cookieName');
    if (cookieName) {
        let cookieValue = Cookie.get(cookieName);

        if (cookieValue) {
            Config.get('vueJs.localization.allowed').hasOwnProperty(cookieValue) ?
                Cookie.set(cookieName, cookieValue, { expires: 400 }) :
                Cookie.remove(cookieName);
        }
        else {
            Cookie.set(cookieName, currentLocale, { expires: 400 });
        }
    }

    App
        .use(I18n)
        .use(PrimeVue, {
            theme: {
                preset: Aura,
                options: {
                    prefix: 'prime-vue',
                    darkModeSelector: false
                }
            }
        })
        .use(ToastService)
        .use(Config)
        .use(new Validator)
        .use(User)
        .use(new FormatterPlugin)
        .use(new BroadcastChannelPlugin)
        .use(new ReverbChannelPlugin)
        .use(Router);

    // Interceptor regisztrálása mind a legacy (window.axios),
    // mind az új axiosInstance számára.
    // Így bármelyiket használjuk (régi komponensek vagy új kód),
    // ugyanaz a request/response logika (401,  302, urlParams, stb.) fog lefutni.
    setAxiosIntercreptor(App, axios);  // legacy (window.axios)
    setAxiosIntercreptor(App, axiosInstance); // új instance

    await Config.setLocale(currentLocale);

    return App;
}

export { I18n };